import React from 'react';
import PropTypes from 'prop-types';
import zxcvbn from 'zxcvbn';

import InputGroup from '@pcid/components/forms/input-group';
import Form from '@pcid/components/forms/form';
import FormMessage from '@pcid/components/forms/form-message';
import InputPassword from '@pcid/components/forms/input-password';
import PasswordStrength from '@pcid/components/forms/password-strength';
import Link from '@pcid/link';
import { AutoFormattedMessage, autoFormatMessage } from '@pcid/string-utils';
import { getTermsURL, getStoreName, getFFConfig, getRPConfig } from '@pcid/components/config-context';
import { localeManager } from '@pcid/locale-utils';
import SubmitButton from '@pcid/components/forms/submit-button';
import track from '@pcid/analytics/track';
import speck, {
	toExist,
	toMatch,
	errorCodes,
	toBeAnEmail,
	toBeChecked,
	toBeAName,
	getPasswordValidateProps,
} from '@pcid/utils/validation';
import constants from '../../actions/track-analytics/constants';

const {
	linkName: { CREATE_ACCOUNT_EMAIL_EXISTS_SIGN_IN },
} = constants;

export const formId = 'create-account';

export const PrivacyPolicyLabel = ({ disabled }) => (
	<span className="create-account__privacy-policy">
		<AutoFormattedMessage
			id="form.createAccount.privacyPolicy"
			values={{
				link: (
					<Link
						to={`https://www.loblaw.ca/${localeManager.getLanguage()}/privacy.html`}
						target="_blank"
						disabled={disabled}
					>
						<AutoFormattedMessage id="form.createAccount.privacy" />
					</Link>
				),
			}}
		/>
	</span>
);

const getPrivacyPolicyAriaLabel = () => ({
	id: 'form.createAccount.privacyPolicy',
	values: {
		link: autoFormatMessage('form.createAccount.privacy'),
	},
});

export const TermsLabel = ({ relyingParty, termsURL = getTermsURL(relyingParty), disabled }) => (
	<span className="create-account-page__terms-and-conditions">
		<AutoFormattedMessage
			id="form.createAccount.termsAndConditions"
			values={{
				relyingParty: autoFormatMessage(getStoreName(relyingParty)),
				link: (
					<Link
						to={termsURL}
						target="_blank"
						disabled={disabled}
					>
						<AutoFormattedMessage id="form.createAccount.terms" />
					</Link>
				),
			}}
		/>
	</span>
);

const getTermsAriaLabel = (relyingParty) => ({
	id: 'form.createAccount.termsAndConditions',
	values: {
		relyingParty: autoFormatMessage(getStoreName(relyingParty)),
		link: autoFormatMessage('form.createAccount.terms'),
	},
});

const syncValidateEmail = (email) => speck(
	email,
	toExist.else(errorCodes.emailRequired),
	toBeAnEmail
);

const CreateAccountForm = ({
	checkEmail,
	values,
	errors,
	relyingParty,
	setEmail,
	validateField,
}) => {
	const emailExists = errors && errors.email && errors.email.id === errorCodes.emailExists.id;
	const { casl } = getFFConfig();
	const { casl: caslRp, caslMsg, collectName } = getRPConfig();
	const termsURL = getTermsURL(relyingParty);
	return (
		<Form
			id={formId}
			className="create-account-form"
			heading="form.createAccount"
			breadcrumb
			showIcon={false}
		>
			{ collectName
				&& (
					<div
						className="create-account-form__name"
					>
						<InputGroup
							name="firstName"
							type="given-name"
							className="create-account-form__firstName"
							label="form.createAccount.firstName"
							autoComplete="given-name"
							validateOnChange
							validateOnBlur
							validate={(firstName) => speck(
								firstName,
								toExist.else(errorCodes.emptyFirstName),
								toBeAName
							)}
							required
						/>
						<InputGroup
							name="lastName"
							type="family-name"
							className="create-account-form__lastName"
							label="form.createAccount.lastName"
							autoComplete="family-name"
							validateOnChange
							validateOnBlur
							validate={(lastName) => speck(
								lastName,
								toExist.else(errorCodes.emptyLastName),
								toBeAName
							)}
							required
						/>
					</div>
				)}
			<InputGroup
				name="email"
				type="email"
				className="create-account-form__email"
				label="common.email"
				autoComplete="username"
				asyncValidate={checkEmail}
				validate={syncValidateEmail}
				onAsyncValidateComplete={() => {
					const { email } = values;
					setEmail(syncValidateEmail(email) ? '' : email);
				}}
				validateOnChange
				showValidatedIcon
				required
				subtext={emailExists
					? (
						<FormMessage type="warning">
							<AutoFormattedMessage
								id="form.createAccount.alreadyHaveAnAccount"
								values={{ email: values.email }}
							/>
							<Link
								to="/login?hidePageView=true"
								theme="button"
								className="create-account-form__sign-in-cta"
								display="block"
								onClick={() => {
									track.customLink({
										linkName: CREATE_ACCOUNT_EMAIL_EXISTS_SIGN_IN,
									});
								}}
							>
								<AutoFormattedMessage id="form.common.signIn" />
							</Link>
							<AutoFormattedMessage id="form.createAccount.alreadyHaveAPcid" />
						</FormMessage>
					)
					: (
						<div className="create-account-form__email-description">
							<AutoFormattedMessage id="form.createAccount.message" />
						</div>
					)}
			/>
			<InputPassword
				name="newPassword"
				type="password"
				className="create-account-form__new-password"
				label="form.createAccount.newPassword"
				autoComplete="new-password"
				{...getPasswordValidateProps({ zxcvbn })}
				onChange={() => setTimeout(() => { validateField('confirmPassword'); })}
				validateOnChange
				validateOnBlur
				allowToggle
				required
				disabled={emailExists}
				subtext={(
					<PasswordStrength
						password={values.newPassword}
						disabled={emailExists}
					/>
				)}
			/>
			<InputPassword
				name="confirmPassword"
				className="create-account-form__confirm-password"
				label="form.createAccount.confirmPassword"
				autoComplete="new-password"
				validate={(confirmPassword) => speck(
					confirmPassword,
					toExist.else(errorCodes.confirmPasswordRequired),
					toMatch(values.newPassword).else(errorCodes.passwordDoesNotMatch)
				)}
				validateOnChange
				validateOnBlur
				allowToggle
				required
			/>
			{(casl && caslRp) && (
				<InputGroup
					name="casl"
					type="checkbox"
					label={caslMsg}
					disabled={emailExists}
				/>
			) }
			{ termsURL
				&& (
					<div className="create-account-form__privacy-terms-description">
						<AutoFormattedMessage
							id="form.createAccount.privacyAndTermsDescription"
						/>
					</div>
				)}
			<InputGroup
				name="privacyPolicy"
				type="checkbox"
				label={<PrivacyPolicyLabel disabled={emailExists} />}
				ariaLabel={getPrivacyPolicyAriaLabel()}
				validate={(privacyPolicy) => speck(
					privacyPolicy,
					toBeChecked.else(errorCodes.privacyPolicyRequired)
				)}
				validateOnChange
				validateOnBlur
				disabled={emailExists}
				required
			/>
			{ termsURL
				&& (
					<InputGroup
						name="termsAndConditions"
						type="checkbox"
						label={<TermsLabel relyingParty={relyingParty} disabled={emailExists} />}
						ariaLabel={getTermsAriaLabel(relyingParty)}
						validate={(termsAndConditions) => speck(
							termsAndConditions,
							toBeChecked.else(errorCodes.termsAndConditionsRequired)
						)}
						validateOnChange
						validateOnBlur
						disabled={emailExists}
						required
					/>
				)}
			<SubmitButton
				messageId="form.createAccount"
				disabled={emailExists}
				analyticsId="create-account"
			/>
		</Form>
	);
};

PrivacyPolicyLabel.propTypes = {
	disabled: PropTypes.bool,
};

TermsLabel.propTypes = {
	relyingParty: PropTypes.string,
	termsURL: PropTypes.string,
	disabled: PropTypes.bool,
};

CreateAccountForm.propTypes = {
	checkEmail: PropTypes.func,
	values: PropTypes.object.isRequired,
	errors: PropTypes.object.isRequired,
	relyingParty: PropTypes.string,
	setEmail: PropTypes.func,
	validateField: PropTypes.func.isRequired,
};

export default CreateAccountForm;
